import React, { useState } from 'react';
import styled from 'styled-components';
import { theme } from '../../styles/theme';
import { RegistrationFormProps, ValidationError } from './types';
import { LoadingSpinner } from '../shared/LoadingSpinner';
import { InterestSelector } from '../Profile/InterestSelector';
import { Interest } from '../Profile/types';
import { DateOfBirthSelector } from '../shared';

// Local-time date <-> string helpers — deliberately avoid new Date(string) /
// .toISOString() here, since both round-trip through UTC and can shift the
// date by one day depending on the browser's timezone offset.
const toLocalISODate = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const parseLocalISODate = (dateString?: string): Date | undefined => {
  if (!dateString) return undefined;
  const [year, month, day] = dateString.split('-').map(Number);
  if (!year || !month || !day) return undefined;
  return new Date(year, month - 1, day);
};

const FormContainer = styled.div`
  width: 100%;
  max-width: 100%;
  margin: 0 auto;
  padding: 1rem;
  
  @media (min-width: 768px) {
    padding: 2rem;
  }
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 3rem;
`;

const Title = styled.h1`
  color: ${theme.colors.text.primary};
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
  background: ${theme.colors.gradients.primary};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const Subtitle = styled.p`
  color: ${theme.colors.text.secondary};
  font-size: 1.1rem;
  line-height: 1.6;
`;

const Form = styled.form`
  width: 100%;
  max-width: 100%;
  background: ${theme.colors.background.card};
  border-radius: ${theme.borderRadius.xl};
  padding: 1rem;
  box-shadow: ${theme.shadows.heavy};
  
  @media (min-width: 768px) {
    padding: 2rem;
  }
`;

const FormSection = styled.div`
  margin-bottom: 2rem;
`;

const SectionTitle = styled.h3`
  color: ${theme.colors.text.primary};
  font-size: 1.3rem;
  font-weight: 600;
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid ${theme.colors.border.light};
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  color: ${theme.colors.text.primary};
  font-weight: 500;
  margin-bottom: 0.5rem;
  font-size: 0.9rem;
`;

const Input = styled.input<{ error?: boolean }>`
  width: 100%;
  padding: 0.75rem 1rem;
  border: 2px solid ${props => props.error ? theme.colors.error : theme.colors.border.medium};
  border-radius: ${theme.borderRadius.md};
  font-size: 1rem;
  transition: all ${theme.transitions.normal};
  background: ${theme.colors.background.secondary};
  
  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
    background: ${theme.colors.background.card};
    box-shadow: 0 0 0 3px rgba(255, 140, 66, 0.1);
  }
  
  &::placeholder {
    color: ${theme.colors.text.muted};
  }
`;

const Select = styled.select<{ error?: boolean }>`
  width: 100%;
  padding: 0.75rem 1rem;
  border: 2px solid ${props => props.error ? theme.colors.error : theme.colors.border.medium};
  border-radius: ${theme.borderRadius.md};
  font-size: 1rem;
  background: ${theme.colors.background.card};
  cursor: pointer;
  transition: all ${theme.transitions.normal};
  
  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
    box-shadow: 0 0 0 3px rgba(255, 140, 66, 0.1);
  }
`;

const TextArea = styled.textarea<{ error?: boolean }>`
  width: 100%;
  padding: 0.75rem 1rem;
  border: 2px solid ${props => props.error ? theme.colors.error : theme.colors.border.medium};
  border-radius: ${theme.borderRadius.md};
  font-size: 1rem;
  min-height: 150px;
  resize: vertical;
  font-family: inherit;
  transition: all ${theme.transitions.normal};
  background: ${theme.colors.background.secondary};
  
  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
    background: ${theme.colors.background.card};
    box-shadow: 0 0 0 3px rgba(255, 140, 66, 0.1);
  }
  
  &::placeholder {
    color: ${theme.colors.text.muted};
  }
`;

const CharCount = styled.div<{ $isValid: boolean }>`
  margin-top: ${theme.spacing.sm};
  font-size: 0.9rem;
  color: ${props => props.$isValid ? theme.colors.text.secondary : theme.colors.error};
  text-align: right;
`;

const LocationGroup = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
  
  @media (min-width: 768px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const MIN_BIO_CHARACTERS = 50;
const MAX_BIO_CHARACTERS = 500;

const ErrorMessage = styled.div`
  color: ${theme.colors.error};
  font-size: 0.8rem;
  margin-top: 0.25rem;
`;

const ActionButtons = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 2rem;
  flex-wrap: wrap;
  gap: 1rem;
`;

const Button = styled.button<{ variant: 'primary' | 'secondary' | 'outline' }>`
  padding: 1rem 2rem;
  border-radius: ${theme.borderRadius.lg};
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all ${theme.transitions.normal};
  border: none;
  min-width: 140px;
  
  ${props => {
    switch (props.variant) {
      case 'primary':
        return `
          background: ${theme.colors.gradients.primary};
          color: ${theme.colors.text.light};
          box-shadow: ${theme.shadows.medium};
          
          &:hover {
            transform: translateY(-2px);
            box-shadow: ${theme.shadows.heavy};
            background: ${theme.colors.hover.primary};
          }
        `;
      case 'secondary':
        return `
          background: ${theme.colors.text.secondary};
          color: ${theme.colors.text.light};
          
          &:hover {
            background: ${theme.colors.text.muted};
            transform: translateY(-2px);
          }
        `;
      case 'outline':
        return `
          background: transparent;
          color: ${theme.colors.text.secondary};
          border: 2px solid ${theme.colors.border.medium};
          
          &:hover {
            border-color: ${theme.colors.primary};
            color: ${theme.colors.primary};
            transform: translateY(-2px);
          }
        `;
      default:
        return '';
    }
  }}
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none !important;
  }
`;

const SpinnerWrapper = styled.span`
  display: inline-flex;
  align-items: center;
  margin-right: 0.5rem;
`;

const GlobalError = styled.div`
  background: rgba(220, 53, 69, 0.1);
  color: ${theme.colors.error};
  padding: 1rem;
  border-radius: ${theme.borderRadius.md};
  margin-bottom: 2rem;
  text-align: center;
  border: 1px solid ${theme.colors.error};
`;

const PasswordStrength = styled.div<{ strength: 'weak' | 'medium' | 'strong' }>`
  margin-top: 0.5rem;
  font-size: 0.8rem;
  
  ${props => {
    switch (props.strength) {
      case 'weak':
        return 'color: #dc3545;';
      case 'medium':
        return 'color: #ffc107;';
      case 'strong':
        return 'color: #28a745;';
      default:
        return 'color: #6c757d;';
    }
  }}
`;

const PasswordStrengthBar = styled.div<{ strength: 'weak' | 'medium' | 'strong' }>`
  height: 4px;
  border-radius: 2px;
  margin-top: 0.25rem;
  transition: all 0.3s ease;
  
  ${props => {
    switch (props.strength) {
      case 'weak':
        return 'background: #dc3545; width: 33%;';
      case 'medium':
        return 'background: #ffc107; width: 66%;';
      case 'strong':
        return 'background: #28a745; width: 100%;';
      default:
        return 'background: #e9ecef; width: 0%;';
    }
  }}
`;

export const RegistrationForm: React.FC<RegistrationFormProps> = ({
  formData,
  onFieldChange,
  onSubmit,
  onBack,
  loading = false,
  error
}) => {
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);

  const validateField = (field: string, value: any): string | null => {
    switch (field) {
      case 'email':
        if (!value) return 'Email is required';
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return 'Please enter a valid email';
        return null;
      case 'password':
        if (!value) return 'Password is required';
        if (value.length < 8) return 'Password must be at least 8 characters';
        return null;
      case 'confirmPassword':
        if (!value) return 'Please confirm your password';
        if (value !== formData.password) return 'Passwords do not match';
        return null;
      case 'dateOfBirth': {
        if (!value) return 'Date of birth is required';
        const age = new Date().getFullYear() - new Date(value).getFullYear();
        if (age < 18) return 'You must be at least 18 years old';
        if (age > 100) return 'Please enter a valid date of birth';
        return null;
      }
      case 'gender':
        if (!value) return 'Gender is required';
        return null;
      case 'firstName':
        if (!value) return 'First name is required';
        return null;
      case 'lastName':
        if (!value) return 'Last name is required';
        return null;
      default:
        return null;
    }
  };

  const getPasswordStrength = (password: string): 'weak' | 'medium' | 'strong' => {
    if (password.length < 6) return 'weak';
    if (password.length < 10) return 'medium';
    return 'strong';
  };

  const handleFieldChange = (field: keyof typeof formData, value: any) => {
    onFieldChange(field, value);

    // Clear validation error for this field
    setValidationErrors(prev => prev.filter(error => error.field !== field));

    // Validate field
    const error = validateField(field, value);
    if (error) {
      setValidationErrors(prev => [...prev.filter(e => e.field !== field), { field, message: error }]);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Validate all required fields
    const errors: ValidationError[] = [];
    const requiredFields: (keyof typeof formData)[] = ['email', 'password', 'confirmPassword', 'firstName', 'lastName'];

    requiredFields.forEach(field => {
      const error = validateField(field, formData[field]);
      if (error) {
        errors.push({ field, message: error });
      }
    });

    if (errors.length > 0) {
      setValidationErrors(errors);
      return;
    }

    onSubmit(formData);
  };

  const getFieldError = (field: string) => {
    return validationErrors.find(error => error.field === field)?.message;
  };

  const passwordStrength = getPasswordStrength(formData.password);

  return (
    <FormContainer>
      <Header>
        <Title>Complete Your Profile</Title>
        <Subtitle>
          Fill in your details to complete your registration and start finding meaningful connections.
        </Subtitle>
      </Header>

      {error && <GlobalError>{error}</GlobalError>}

      <Form onSubmit={handleSubmit}>
        <FormSection>
          <SectionTitle>Account Information</SectionTitle>

          <FormGroup>
            <Label htmlFor="email">Email Address *</Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => handleFieldChange('email', e.target.value)}
              placeholder="Enter your email"
              error={!!getFieldError('email')}
              disabled={loading}
            />
            {getFieldError('email') && <ErrorMessage>{getFieldError('email')}</ErrorMessage>}
          </FormGroup>

          <FormGroup>
            <Label htmlFor="password">Password *</Label>
            <Input
              id="password"
              type="password"
              value={formData.password}
              onChange={(e) => handleFieldChange('password', e.target.value)}
              placeholder="Create a strong password"
              error={!!getFieldError('password')}
              disabled={loading}
            />
            {formData.password && (
              <>
                <PasswordStrength strength={passwordStrength}>
                  Password strength: {passwordStrength}
                </PasswordStrength>
                <PasswordStrengthBar strength={passwordStrength} />
              </>
            )}
            {getFieldError('password') && <ErrorMessage>{getFieldError('password')}</ErrorMessage>}
          </FormGroup>

          <FormGroup>
            <Label htmlFor="confirmPassword">Confirm Password *</Label>
            <Input
              id="confirmPassword"
              type="password"
              value={formData.confirmPassword}
              onChange={(e) => handleFieldChange('confirmPassword', e.target.value)}
              placeholder="Confirm your password"
              error={!!getFieldError('confirmPassword')}
              disabled={loading}
            />
            {getFieldError('confirmPassword') && <ErrorMessage>{getFieldError('confirmPassword')}</ErrorMessage>}
          </FormGroup>
        </FormSection>

        <FormSection>
          <SectionTitle>Personal Details</SectionTitle>

          <FormGroup>
            <Label htmlFor="firstName">First Name *</Label>
            <Input
              id="firstName"
              type="text"
              value={formData.firstName || ''}
              onChange={(e) => handleFieldChange('firstName', e.target.value)}
              placeholder="Enter your first name"
              error={!!getFieldError('firstName')}
              disabled={loading}
            />
            {getFieldError('firstName') && <ErrorMessage>{getFieldError('firstName')}</ErrorMessage>}
          </FormGroup>

          <FormGroup>
            <Label htmlFor="lastName">Last Name *</Label>
            <Input
              id="lastName"
              type="text"
              value={formData.lastName || ''}
              onChange={(e) => handleFieldChange('lastName', e.target.value)}
              placeholder="Enter your last name"
              error={!!getFieldError('lastName')}
              disabled={loading}
            />
            {getFieldError('lastName') && <ErrorMessage>{getFieldError('lastName')}</ErrorMessage>}
          </FormGroup>

          <FormGroup>
            <Label htmlFor="dateOfBirth">Date of Birth *</Label>
            <DateOfBirthSelector
              value={parseLocalISODate(formData.dateOfBirth)}
              onChange={(date) => handleFieldChange('dateOfBirth', toLocalISODate(date))}
              hasError={!!getFieldError('dateOfBirth')}
              disabled={loading}
            />
            {getFieldError('dateOfBirth') && <ErrorMessage>{getFieldError('dateOfBirth')}</ErrorMessage>}
          </FormGroup>

          <FormGroup>
            <Label htmlFor="gender">Gender *</Label>
            <Select
              id="gender"
              value={formData.gender || ''}
              onChange={(e) => handleFieldChange('gender', e.target.value)}
              error={!!getFieldError('gender')}
              disabled={loading}
            >
              <option value="">Select gender</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
              <option value="non-binary">Non-binary</option>
              <option value="other">Other</option>
            </Select>
            {getFieldError('gender') && <ErrorMessage>{getFieldError('gender')}</ErrorMessage>}
          </FormGroup>
        </FormSection>

        <ActionButtons>
          <Button
            type="button"
            variant="outline"
            onClick={onBack}
            disabled={loading}
          >
            Back
          </Button>

          <Button
            type="submit"
            variant="primary"
            disabled={loading || validationErrors.length > 0}
          >
            {loading && (
              <SpinnerWrapper>
                <LoadingSpinner size="small" color={theme.colors.text.light} />
              </SpinnerWrapper>
            )}
            Create Account
          </Button>
        </ActionButtons>
      </Form>
    </FormContainer>
  );
};
